import { createStore, useStore } from "zustand";
import { immer } from "zustand/middleware/immer";
import { subscribeWithSelector } from "zustand/middleware";
import { canPlayType } from "@/lib/utils";
import type { PlaybackStatus } from "../ports";
import type { ResolvedTrackModel, TrackModel } from "@coffitivity/shared";

type PlaybackState = {
	currentTrack: ResolvedTrackModel | TrackModel | null;
	previousTrack: ResolvedTrackModel | TrackModel | null;
	isUnlocked: boolean;
	isCurrentTrackDraft: boolean;
	masterVolume: number;
	previousMasterVolume: number;
	preferredMimeType: "mp3" | "ogg";
	status: PlaybackStatus;
};

type PlaybackActions = {
	clearCurrentTrack: () => void;
	moveCurrentTrackToPrevious: () => void;
	movePreviousTrackToCurrent: () => void;
	setIsUnlocked: (isUnlocked: boolean) => void;
	setIsCurrentTrackDraft: (isDraft: boolean) => void;
	setPreferredMimeType: (preferredMimeType: "mp3" | "ogg") => void;
	setCurrentTrack: (track: ResolvedTrackModel | TrackModel) => void;
	updateCurrentBlend: (
		trackId: string,
		blend: ResolvedTrackModel["blend"],
	) => void;
	setCurrentTrackStatus: (status: PlaybackStatus) => void;
	setVolume: (volume: number, trackId?: string) => void;
	toggleMute: () => void;
};

export type PlaybackStoreProps = PlaybackState & PlaybackActions;

export const playbackStore = createStore<PlaybackStoreProps>()(
	subscribeWithSelector(
		immer((set) => ({
			currentTrack: null,
			previousTrack: null,
			isUnlocked: false,
			isCurrentTrackDraft: false,
			masterVolume: 1,
			previousMasterVolume: 1,
			preferredMimeType: canPlayType("audio/ogg") ? "ogg" : "mp3",
			status: "UNINITIALIZED",
			clearCurrentTrack: () =>
				set({ currentTrack: null, isCurrentTrackDraft: false }),
			moveCurrentTrackToPrevious: () =>
				set((state) => {
					if (state.currentTrack) {
						state.previousTrack = state.currentTrack;
						state.currentTrack = null;
					}
				}),
			movePreviousTrackToCurrent: () =>
				set((state) => {
					if (state.previousTrack) {
						state.currentTrack = state.previousTrack;
						state.previousTrack = null;
					}
				}),
			setIsUnlocked: (isUnlocked) => set({ isUnlocked }),
			setIsCurrentTrackDraft: (isDraft) =>
				set({ isCurrentTrackDraft: isDraft }),
			setPreferredMimeType: (preferredMimeType) => set({ preferredMimeType }),
			setCurrentTrack: (track) => {
				set({ currentTrack: structuredClone(track) });
			},
			updateCurrentBlend: (trackId, blend) =>
				set((state) => {
					if (
						state.currentTrack?.id !== trackId ||
						state.currentTrack.type !== "BLEND"
					) {
						return;
					}

					state.currentTrack = {
						...state.currentTrack,
						blend: structuredClone(blend),
					} as ResolvedTrackModel;
				}),
			setCurrentTrackStatus: (status: PlaybackStatus) =>
				set((state) => {
					state.status = status;
				}),
			setVolume: (volume) =>
				set((state) => {
					// set the last master volume value before changing it
					state.previousMasterVolume = state.masterVolume;
					// Update the master volume
					state.masterVolume = volume;
				}),
			toggleMute: () =>
				set((state) => {
					if (state.masterVolume > 0) {
						state.previousMasterVolume = state.masterVolume;
						state.masterVolume = 0;
					} else {
						state.masterVolume = state.previousMasterVolume;
					}
				}),
		})),
	),
);

export type PlaybackStore = typeof playbackStore;

// hook to use the store in react components
export const usePlaybackStore = () => {
	const store = useStore(playbackStore);
	return store;
};
